import React, {
  memo,
  useEffect,
  useRef,
} from 'react';

import { Typography } from 'antd';
import { TbMessageSearch } from 'react-icons/tb';
import InfiniteScroll from 'react-infinite-scroller';

import Loader from '../../../../components/V2/Loader/Loader';
import { useTheme } from '../../../../context/ThemeProvider';
import { checkTQConnectAccess } from '../../../../shared/tqConnectUtils';
import {
  ITQServiceRequestResponse,
} from '../../../../types/tqConnectTypes/service-requests';
import NoDataFound from '../../../Feed/components/NoDataFound/NoDataFound';
import ResponseCard from './ResponseCard';

interface Props {
  serviceRequestId: string;
  isMyRequest: boolean;
  hasMyResponse: boolean;
  list: ITQServiceRequestResponse[];
  total: number;
  loading: boolean;
  isFetchingNextPage: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onViewResponse: (response: ITQServiceRequestResponse) => void;
}

const ResponsesList: React.FC<Props> = ({
  serviceRequestId,
  isMyRequest,
  hasMyResponse,
  list,
  total,
  loading,
  isFetchingNextPage,
  hasMore,
  onLoadMore,
  onViewResponse: handleViewResponse,
}) => {
  const { colors: COLORS } = useTheme();

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const { userAccessType } = checkTQConnectAccess();

  useEffect(() => {
    const element = document.getElementById(
      `service-request-responses-${serviceRequestId}`,
    );

    if (element) {
      scrollContainerRef.current = element;
    }
  }, [serviceRequestId]);

  return (
    <>
      <Typography.Title level={5} style={{ margin: 16 }}>
        {hasMyResponse ? 'Other responses' : 'Responses'}{' '}
        {total > 0 ? `(${total})` : ''}
      </Typography.Title>

      <div className="responses-list">
        <InfiniteScroll
          pageStart={0}
          loadMore={onLoadMore}
          hasMore={hasMore}
          useWindow={false}
          getScrollParent={() => scrollContainerRef.current}
          loader={
            (!loading && (hasMore || isFetchingNextPage) ? (
              <Loader type="spinner" color={COLORS.ICON} />
            ) : null) as JSX.Element
          }>
          {loading ? <Loader type="spinner" color={COLORS.ICON} /> : null}

          {list.map((response) => (
            <ResponseCard
              key={`service-request-response-${response._id}`}
              response={response}
              respondedBy={response.respondedBy}
              canView={isMyRequest}
              onViewResponse={() => {
                handleViewResponse(response);
              }}
            />
          ))}

          {total > 0 && !loading && !hasMore ? (
            <Typography.Paragraph
              style={{
                width: '100%',
                textAlign: 'center',
                marginBlock: 32,
              }}>
              That’s it! No more responses to show.
            </Typography.Paragraph>
          ) : null}
        </InfiniteScroll>

        {total === 0 && !loading && !hasMore ? (
          <NoDataFound
            iconElement={<TbMessageSearch size={96} color={COLORS.TEXT5} />}
            title={
              hasMyResponse
                ? 'No other responses yet!'
                : 'No responses received yet!'
            }
            subtitle={
              !hasMyResponse && !isMyRequest && userAccessType === 'customer'
                ? 'Be the first to respond to this request.'
                : (isMyRequest &&
                    `Responses will appear here once people offer to help on your request.`) ||
                  `Responses will appear here once people respond to this request.`
            }
            style={{
              width: '100%',
              padding: 16,
              paddingTop: 48,
            }}
          />
        ) : null}
      </div>
    </>
  );
};

export default memo(ResponsesList);
